import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { GridOn as ChartIcon, ShowChart as PeriodontalIcon } from '@mui/icons-material';
import DentalChartPanel from './DentalChartPanel';
import PatientPeriodontalTab from './PatientPeriodontalTab';

export type PatientOdontogramSection = 'chart' | 'periodontal';

export interface PatientOdontogramTabProps {
  patientId: string;
  unitId?: string | null;
  linkedProfessionalId?: string | null;
  /** Seção aberta ao montar (ex.: deep link legado `periodontal`). */
  initialSection?: PatientOdontogramSection;
  /** Controlado pelo painel ao redirecionar aba legada `periodontal`. */
  section?: PatientOdontogramSection;
  onSectionChange?: (section: PatientOdontogramSection) => void;
}

const PatientOdontogramTab: React.FC<PatientOdontogramTabProps> = ({
  patientId,
  unitId,
  linkedProfessionalId,
  initialSection = 'chart',
  section: controlledSection,
  onSectionChange,
}) => {
  const [internalSection, setInternalSection] = useState<PatientOdontogramSection>(initialSection);
  const section = controlledSection ?? internalSection;

  useEffect(() => {
    setInternalSection(initialSection);
  }, [patientId, initialSection]);

  const setSection = (next: PatientOdontogramSection) => {
    if (controlledSection === undefined) setInternalSection(next);
    onSectionChange?.(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 2 },
          pb: 0,
          borderBottom: '1px solid #E2E8F0',
          bgcolor: '#FAFAFA',
        }}
      >
        <Tabs
          value={section}
          onChange={(_, value: PatientOdontogramSection) => setSection(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#64748B',
            },
            '& .Mui-selected': { color: '#0F766E' },
            '& .MuiTabs-indicator': { bgcolor: '#0D9488', height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab value="chart" label="Mapa dentário" icon={<ChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab value="periodontal" label="Periograma" icon={<PeriodontalIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {section === 'chart' ? (
          <DentalChartPanel patientId={patientId} unitId={unitId} dense embedded />
        ) : (
          <PatientPeriodontalTab
            patientId={patientId}
            unitId={unitId}
            linkedProfessionalId={linkedProfessionalId}
            embedded
          />
        )}
      </Box>
    </Box>
  );
};

export default PatientOdontogramTab;
